import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import { useQuery } from "@apollo/client";
import { TYPE_WELLS } from "constants/settings.constants";
import { arrayOrEmpty } from "utils";
import { v4 as uuidv4 } from "uuid";

import { useAllGeomBinsQuery } from "hooks";

import { FOLDERS } from "api/userArps";

import { useForecastFolderFetcher } from "components/arps/hooks";
import { useProjectContext } from "components/project/projects/context";

import {
  GetRingFencesByScenarioKey,
  useCreateRingFence,
  useDeleteRingFence,
  useGetRingFencesByScenario,
  useUpdateRingFence
} from ".";
import { ScenarioStickListKey } from "..";
import { usePadScenario } from "../../contexts/usePadScenario";
import { CreateRingFenceRequest, RingFence } from "../../models/ringFence";
import useGeomBinData from "../useGeomBinData";

export interface GeomBinNode {
  id: string;
  name: string;
  binId?: string;
  order: number;
  geom: string;
  isDeleted: boolean;
  color: string;
  strokeColor: string;
  opacity: number;
  thickness: number;
}

export interface TypeWell {
  id: string;
  name: string;
}

export interface RingFenceTableRow extends RingFence {
  isEditing?: boolean;
  isNew?: boolean;
  tempName?: string;
  tempGeomBinNodeId?: string;
  tempTypeWellId?: string;
  error?: string;
}

export function useRingFenceController() {
  const { state } = usePadScenario();
  const { selectedProjectId } = useProjectContext();
  const scenarioId = state.scenario?._id?.$oid;
  const queryClient = useQueryClient();
  // State
  const [selectedGeometryBin, setSelectedGeometryBin] = useState<string>("");
  const [selectedPolygon, setSelectedPolygon] = useState<string>("");
  const [tableData, setTableData] = useState<RingFenceTableRow[]>([]);

  // Hooks
  const { data: ringFences = [], isLoading: isLoadingRingFences } =
    useGetRingFencesByScenario(scenarioId);
  const { data: geomBinsData } = useAllGeomBinsQuery({
    selectedProjectId
  });
  const allGeomBins = geomBinsData?.allGeomBins;

  // Get polygons from selected geometry bin
  const { data: selectedGeomBinData, isLoading: isLoadingGeomBin } =
    useGeomBinData(selectedGeometryBin);

  const createRingFence = useCreateRingFence();
  const updateRingFence = useUpdateRingFence();
  const deleteRingFence = useDeleteRingFence();

  // Fetch type wells data using the actual API
  const { getForecastFoldersFromFoldersData } = useForecastFolderFetcher(TYPE_WELLS);
  const { data: foldersData, loading: isLoadingTypeWells } = useQuery(FOLDERS, {
    variables: {
      req: {
        projectId: selectedProjectId,
        type: TYPE_WELLS
      }
    },
    skip: !selectedProjectId
  });

  // Extract type wells from folders data
  const typeWells: TypeWell[] = useMemo(() => {
    if (!foldersData) return [];

    const folders = getForecastFoldersFromFoldersData(foldersData, TYPE_WELLS);
    const wells: TypeWell[] = [];

    folders.forEach((folder) => {
      folder.children.forEach((child) => {
        wells.push({
          id: child.id,
          name: child.title
        });
      });
    });

    wells.sort((a, b) => a.name.localeCompare(b.name));
    return wells;
  }, [foldersData, getForecastFoldersFromFoldersData]);

  // Update table data when ring fences change
  useEffect(() => {
    const mappedData: RingFenceTableRow[] = ringFences.map((rf) => ({
      ...rf,
      isEditing: false,
      isNew: false
    }));
    setTableData(mappedData);
  }, [ringFences]);

  // Populate geometry bin from first ring fence when scenario changes
  useEffect(() => {
    // If ring fences exist, populate geometry bin from the first ring fence
    if (ringFences.length > 0) {
      const firstRingFence = ringFences[0];
      if (firstRingFence.geomBinId) {
        // Directly use the geomBinId from the ring fence
        setSelectedGeometryBin(firstRingFence.geomBinId);
      }
    } else {
      setSelectedGeometryBin("");
    }
  }, [scenarioId, ringFences]);

  // Reset polygon selection when geometry bin changes
  useEffect(() => {
    if (selectedGeometryBin) {
      setSelectedPolygon(""); // Reset polygon selection when bin changes
    }
  }, [selectedGeometryBin]);

  /**
   * Returns the list of geometry bin nodes (polygons) for the selected geometry bin.
   *
   * @param includeUsed - If true, includes all polygons in the geometry bin, even those already used by a ring fence.
   *                      If false, only polygons not already assigned to a ring fence are returned.
   *                      Defaults to true.
   */
  const getGeomBinNodes = useCallback(
    (includeUsed: boolean = true): GeomBinNode[] => {
      if (!selectedGeomBinData?.nodes) return [];
      const nodesAlreadyUsed = ringFences.map((rf) => rf.geomBinNodeId ?? "");
      return selectedGeomBinData.nodes
        .filter((node) => includeUsed || !nodesAlreadyUsed.includes(node.id))
        .map((node) => ({
          id: node.id,
          name: node.name,
          binId: node.binId,
          order: node.order,
          geom: node.geom,
          isDeleted: node.isDeleted,
          color: node.color,
          strokeColor: node.strokeColor,
          opacity: node.opacity,
          thickness: node.thickness
        }));
    },
    [selectedGeomBinData?.nodes, ringFences]
  );

  const handleAddRingFence = () => {
    if (!selectedGeometryBin || !selectedPolygon) {
      return;
    }

    // Get the selected polygon's name to use as default ring fence name
    const selectedPolygonName = getGeomBinNodeName(selectedPolygon);

    const newRow: RingFenceTableRow = {
      _id: { $oid: uuidv4() },
      scenarioId: scenarioId || "",
      name: selectedPolygonName,
      geomBinNodeId: selectedPolygon,
      typeWellId: "",
      isEditing: true,
      isNew: true,
      tempName: selectedPolygonName,
      tempGeomBinNodeId: selectedPolygon,
      tempTypeWellId: ""
    };

    setTableData([...tableData, newRow]);
  };

  const handleSave = async (record: RingFenceTableRow) => {
    try {
      if (!record.tempGeomBinNodeId || !record.tempTypeWellId) {
        // Set error on the row
        setTableData(
          tableData.map((row) =>
            row._id?.$oid === record._id?.$oid
              ? { ...row, error: "Polygon and Type Well are required" }
              : row
          )
        );
        return;
      }

      if (record.isNew) {
        // Create new ring fence
        const createRequest: CreateRingFenceRequest = {
          scenarioId: scenarioId || "",
          name: record.tempName || undefined,
          geomBinId: selectedGeometryBin,
          geomBinNodeId: record.tempGeomBinNodeId || "",
          typeWellId: record.tempTypeWellId || ""
        };

        await createRingFence.mutateAsync(createRequest);
      } else {
        // Update existing ring fence
        if (record._id) {
          await updateRingFence.mutateAsync({
            _id: record._id,
            scenarioId: record.scenarioId,
            name: record.tempName || undefined,
            geomBinId: record.geomBinId || selectedGeometryBin,
            geomBinNodeId: record.tempGeomBinNodeId || "",
            typeWellId: record.tempTypeWellId || ""
          });
        }
      }

      await queryClient.invalidateQueries([GetRingFencesByScenarioKey, scenarioId]);
      await queryClient.invalidateQueries([ScenarioStickListKey]);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save ring fence";
      // Set error on the row
      setTableData(
        tableData.map((row) =>
          row._id?.$oid === record._id?.$oid ? { ...row, error: errorMessage } : row
        )
      );
    }
  };

  const handleCancel = (record: RingFenceTableRow) => {
    if (record.isNew) {
      // Remove new row
      setTableData(tableData.filter((row) => row._id?.$oid !== record._id?.$oid));
    } else {
      // Reset editing state
      setTableData(
        tableData.map((row) =>
          row._id?.$oid === record._id?.$oid
            ? {
                ...row,
                isEditing: false,
                tempName: undefined,
                tempGeomBinNodeId: undefined,
                tempTypeWellId: undefined
              }
            : row
        )
      );
    }
  };

  const handleEdit = (record: RingFenceTableRow) => {
    setTableData(
      tableData.map((row) =>
        row._id?.$oid === record._id?.$oid
          ? {
              ...row,
              isEditing: true,
              tempName: row.name || "",
              tempGeomBinNodeId: row.geomBinNodeId || "",
              tempTypeWellId: row.typeWellId || ""
            }
          : row
      )
    );
  };

  const handleDelete = async (record: RingFenceTableRow) => {
    try {
      if (record.isNew) {
        handleCancel(record);
        return;
      }

      if (record._id) {
        await deleteRingFence.mutateAsync({
          ringFenceId: record._id.$oid,
          scenarioId: record.scenarioId
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete ring fence";
      // Set error on the row
      setTableData(
        tableData.map((row) =>
          row._id?.$oid === record._id?.$oid ? { ...row, error: errorMessage } : row
        )
      );
    }
  };

  const handleFieldChange = (recordId: string, field: string, value: string) => {
    setTableData(
      tableData.map((row) =>
        row._id?.$oid === recordId
          ? { ...row, [`temp${field.charAt(0).toUpperCase() + field.slice(1)}`]: value }
          : row
      )
    );
  };

  const getGeomBinNodeName = useCallback(
    (nodeId: string): string => {
      const nodes = getGeomBinNodes(true);
      const node = nodes.find((n) => n.id === nodeId);
      return node?.name || "Unknown Polygon";
    },
    [getGeomBinNodes]
  );

  const getTypeWellName = useCallback(
    (typeWellId: string): string => {
      const typeWell = typeWells.find((tw) => tw.id === typeWellId);
      return typeWell?.name || typeWellId;
    },
    [typeWells]
  );

  return {
    // State
    scenarioId,
    selectedGeometryBin,
    setSelectedGeometryBin,
    selectedPolygon,
    setSelectedPolygon,
    tableData,

    // Data
    allGeomBins: arrayOrEmpty(allGeomBins),
    typeWells,
    isLoadingRingFences,
    isLoadingGeomBin,
    isLoadingTypeWells,

    // Mutation states
    isCreating: createRingFence.isLoading,
    isUpdating: updateRingFence.isLoading,
    isDeleting: deleteRingFence.isLoading,

    // Handlers
    handleAddRingFence,
    handleSave,
    handleCancel,
    handleEdit,
    handleDelete,
    handleFieldChange,

    // Helper functions
    getGeomBinNodes,
    getGeomBinNodeName,
    getTypeWellName
  };
}
